/**
 * Economy (§1.5): income per minute, accrued every tick.
 *   income = (stipend + perAreaW·ΣareaW[rad<0.5] + perPop·Σpop
 *             + Σcities cityPerLevel·L·clamp(pop/capEff)·(1−rad)) × UNDERDOG × (easy bot 0.85)
 */
import { BALANCE, TICK_HZ } from '../balance.js';
import { capEff, isAlive, structureActive } from '../entities.js';
import type { MatchState } from '../types.js';

const E = BALANCE.economy;

/** Income per minute of every player from the current state (index PlayerId). */
export function computeIncome(state: MatchState): Float64Array {
  const w = state.world;
  const np = state.players.length;
  const areaInc = new Float64Array(np + 1);
  const popSum = new Float64Array(np + 1);
  const areaAll = new Float64Array(np + 1);
  let totalArea = 0;
  for (let h = 0; h < w.landCount; h++) {
    const o = state.owner[h];
    totalArea += w.areaW[h];
    if (!o) continue;
    areaAll[o] += w.areaW[h];
    popSum[o] += state.pop[h];
    if (state.rad[h] < E.areaRadMax) areaInc[o] += w.areaW[h];
  }
  const cityInc = new Float64Array(np + 1);
  for (const e of state.entities.values()) {
    if (e.kind !== 'city' || e.owner === 0 || !structureActive(state, e)) continue;
    const h = e.hex;
    const fill = Math.min(1, Math.max(0, state.pop[h] / capEff(state, h)));
    cityInc[e.owner] += E.cityPerLevel * e.level * fill * (1 - state.rad[h]);
  }
  let alive = 0;
  let shareSum = 0;
  for (const p of state.players) {
    if (!isAlive(p)) continue;
    alive++;
    shareSum += areaAll[p.id] / totalArea;
  }
  const avgShare = alive > 0 ? shareSum / alive : 0;
  const out = new Float64Array(np + 1);
  for (const p of state.players) {
    if (!isAlive(p)) continue;
    let inc = E.stipend + E.perAreaW * areaInc[p.id] + E.perPop * popSum[p.id] + cityInc[p.id];
    if (areaAll[p.id] / totalArea < E.underdogThreshold * avgShare) inc *= E.underdogMult;
    if (p.isBot && p.difficulty === 'easy') inc *= E.easyBotMult;
    out[p.id] = inc;
  }
  return out;
}

export function economySystem(state: MatchState): void {
  const inc = computeIncome(state);
  for (const p of state.players) {
    if (!isAlive(p)) {
      p.incomePerMin = 0;
      continue;
    }
    p.incomePerMin = inc[p.id];
    const gain = inc[p.id] / (60 * TICK_HZ);
    p.credits += gain;
    state.stats.players[p.id - 1].creditsEarned += gain;
  }
}
